import { formatDate, formatDecimalPlace, formatQuantity } from "common/formatters";
import Contract, { ContractType, getContractTypeName } from "domain/contract";
import React from "react";
import { getPaymentOptionDisplayName } from "domain/paymentOption";
import { getProducerDisplayName } from "features/settings/producers/producer";
import { getTraderDisplayName } from "features/settings/traders/trader";
import { getDeliveryPeriodDisplayName } from "domain/deliveryPeriod";
import { getPriceLabelForContract } from "common/formatters/strings";
import { useSelector } from "react-redux";
import IAppStore from "app/IAppStore";
import { getTotalFees } from "./helpers";
import { getProducerAccountDisplayName } from "features/settings/producers/producerAccount";
import contractTypeHelper from "domain/contractTypeHelper";
import unitHelper from "domain/unitHelper";
import useTranslators from "common/translation/useTranslators";
import useUnitConversionRates from "features/settings/commodities/useUnitConversionRates";
import { usePaymentOptions } from "features/merchants/usePaymentOptions";
import UiCustomization from "domain/uiCustomization";
import DetailedList, { DetailRow } from "common/ui/DetailedList";
import useActiveMerchant from "features/merchants/useActiveMerchant";
import { isDeferPayInterestEnabled } from "domain/merchant";
import { useCustomerWillSellText } from "common/formatters/useCustomerWillSellText";
import { localizeOfferContract as localize } from "features/localization/localizeService";
import * as NP from "number-precision";

type ContractDetailsConfirmationProps = {
    contract: Contract;
    errors: string[];
    uiCustomization: UiCustomization;
};

const ContractDetailsConfirmation: React.FC<ContractDetailsConfirmationProps> = (props: ContractDetailsConfirmationProps) => {
    const customerWillSellText = useCustomerWillSellText();
    const isCustomer = useSelector((m: IAppStore) => m.userState.isCustomer);
    const activeMerchant = useActiveMerchant();
    const unitsConversionRates = useUnitConversionRates();
    const paymentOptionsDetails = usePaymentOptions();
    const showLocalUOM = props.contract?.commodity?.baseUnit.id !== props.contract?.commodity?.localUnit.id;
    const quantityPrecision = props.uiCustomization.offersContractsQuantityDecimalPrecision;
    const isBushelOnly = props.contract.contractType === ContractType.BushelOnly;

    const translators = useTranslators();

    const totalFees: number = getTotalFees(props.contract);

    function getPriceText(price: number) {
        return unitHelper.getPriceText(price, showLocalUOM, props.contract.contractCurrency.code, props.contract.commodity, unitsConversionRates);
    }

    return <>
        <DetailedList errors={props.errors}>
            <DetailRow highlight label="Type" value={getContractTypeName(props.contract.contractType)} />
            {isBushelOnly && <DetailRow label="Bushel Only Type" value={props.contract.contractExtendedType?.name} />}
            <DetailRow label="Customer" value={getProducerDisplayName(props.contract.producer)} />
            <DetailRow label="Account ID" value={getProducerAccountDisplayName(props.contract.customerAccount)} />
            {props.contract.erpContractId && <DetailRow label="ERP Contract ID" value={props.contract.erpContractId} />}
            <DetailRow highlight label={customerWillSellText.label} value={customerWillSellText.formatValue(props.contract.customerWillSell)} />
            <DetailRow label="Commodity" value={props.contract.commodity.name} />
            {activeMerchant.generalSettings.displayContractLocation && <DetailRow label="Contract Location" value={props.contract.contractLocation?.name ?? 'Determine Later'} />}
            {activeMerchant.generalSettings.displayInventoryLocation && <DetailRow label="Inventory Location" value={props.contract.inventoryLocation?.name ?? 'Determine Later'} />}
            <DetailRow label="Delivery Location" value={props.contract.location?.name ?? 'Determine Later'} />
            {activeMerchant.generalSettings.displayRegion && props.contract.region != null && <DetailRow label="Region" value={props.contract.region.name} />}
            {props.contract.deliveryMethod != null && <DetailRow label="Delivery Method" value={translators.deliveryMethods.translate(props.contract.deliveryMethod)} />}
            {props.contract.freightFee != null && <DetailRow label="Freight Fee" value={formatDecimalPlace(props.contract.freightFee)} />}
            {!isBushelOnly && props.contract.deliveryPeriod && <DetailRow label="Delivery Period" value={getDeliveryPeriodDisplayName(props.contract.deliveryPeriod)} />}
            {props.contract.fees.length > 0 && <DetailRow label="Total Fees" value={getPriceText(totalFees)} />}
            {props.contract.futuresContract && <DetailRow label="Symbol" value={props.contract.futuresContract.symbol} />}
            {contractTypeHelper.hasBasis(props.contract.contractType) && <DetailRow label="Basis" value={props.contract.basis == null ? '--' : formatDecimalPlace(props.contract.basis)} />}
            {contractTypeHelper.hasBasis(props.contract.contractType) && <DetailRow label={localize("Details_Label_Actual_Basis")} value={props.contract.basis == null ? "--" : formatDecimalPlace(NP.plus(props.contract.basis ?? 0, props.contract.pushedBasis ?? 0))} data-testid="review-actual-basis" />}
            {props.contract.cashPrice != null && <DetailRow highlight label={getPriceLabelForContract(props.contract.contractType)} value={getPriceText(props.contract.cashPrice)} />}
            {props.contract.cashPrice != null && <DetailRow label="Final Price" value={getPriceText(props.contract.finalPrice)} />}
            {props.contract.futuresPrice != null && (isBushelOnly || contractTypeHelper.isHta(props.contract.contractType)) &&
                <DetailRow highlight
                    label={getPriceLabelForContract(props.contract.contractType)} value={`${formatDecimalPlace(props.contract.futuresPrice)} ${props.contract.contractCurrency.code}`} />}
            <DetailRow highlight label={props.contract.contractType === ContractType.Basis ? "Contracted Quantity" : "Quantity"}
                value={showLocalUOM
                    ? unitHelper.getQuantityTextWithUnits(props.contract.quantityContracted, props.contract.commodity, unitsConversionRates, quantityPrecision)
                    : `${formatQuantity(props.contract.quantityContracted, quantityPrecision)} ${props.contract.commodity.localUnit.abbreviation}`} />
            {!contractTypeHelper.isCash(props.contract.contractType) && <DetailRow label="Final Pricing Date" value={formatDate(props.contract.finalPricingDate)} />}
            <DetailRow label="Trader" value={getTraderDisplayName(props.contract.trader)} />
            {!isBushelOnly && activeMerchant.generalSettings.displayPaymentOption && <DetailRow label="Payment Option" value={getPaymentOptionDisplayName(props.contract.paymentOption, paymentOptionsDetails)} />}
            {props.contract.deferredPayDate && <DetailRow label="Pay Date" value={formatDate(props.contract.deferredPayDate)} />}
            {isDeferPayInterestEnabled(activeMerchant) && props.contract.interestStartDate && <DetailRow label="Interest Start Date" value={formatDate(props.contract.interestStartDate)} />}
            {props.contract.interestRate != null && <DetailRow label="Interest Rate" value={`${props.contract.interestRate}%`} />}
            {props.contract.deliveryTerms && <DetailRow label="Delivery Terms" value={props.contract.deliveryTerms} />}
            {props.contract.customerContractId && <DetailRow label="Cust. Contract ID" value={props.contract.customerContractId} />}
        </DetailedList>

        {props.contract.comments && <>
            <h3>Comments</h3>
            <p>{props.contract.comments}</p>
        </>}

        {isCustomer && <div className="terms">
            <h5 className="terms-heading">Terms & Conditions</h5>
            <p>By clicking Submit, you are committing to a firm obligation between seller and buyer.</p>
        </div>}
    </>;
};

export default ContractDetailsConfirmation;
